import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { AlertCircle, Trophy, Home, User, Target } from 'lucide-react';
import api from '../../services/api';

const EventMatches = ({ eventId }) => {
  const [expandedMatch, setExpandedMatch] = useState(null);

  const { data: matchesData, isLoading, error } = useQuery(
    ['eventMatches', eventId],
    () => api.get(`/kpis/events/${eventId}/matches`).then(res => res.data),
    {
      enabled: !!eventId,
      staleTime: 30000
    }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center space-x-3 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
        <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
        <div>
          <p className="text-sm font-medium text-red-800 dark:text-red-200">
            Error al cargar los partidos
          </p>
          <p className="text-sm text-red-700 dark:text-red-300">
            {error?.message || 'Intenta nuevamente'}
          </p>
        </div>
      </div>
    );
  }

  const matches = matchesData?.matches || [];

  if (matches.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Trophy className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
        <p className="text-gray-600 dark:text-gray-400 text-lg">
          No hay partidos registrados aún
        </p>
      </div>
    );
  }

  // Agrupar matches por ciclo
  const matchesByCycle = matches.reduce((acc, match) => {
    const cycle = match.cycle || 1;
    if (!acc[cycle]) {
      acc[cycle] = [];
    }
    acc[cycle].push(match);
    return acc;
  }, {});

  const getResultColor = (result) => {
    switch (result) {
      case 'draw':
        return 'bg-blue-100 dark:bg-blue-900/30 border-l-4 border-blue-500';
      case 'no_draw':
        return 'bg-red-100 dark:bg-red-900/30 border-l-4 border-red-500';
      default:
        return 'bg-gray-100 dark:bg-gray-800 border-l-4 border-gray-500';
    }
  };

  const getResultBadgeColor = (result) => {
    switch (result) {
      case 'draw':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300';
      case 'no_draw':
        return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/40 dark:text-gray-300';
    }
  };

  const getResultLabel = (result) => {
    switch (result) {
      case 'draw':
        return 'EMPATE';
      case 'no_draw':
        return 'SIN EMPATE';
      default:
        return 'PENDIENTE';
    }
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center space-x-3">
        <Trophy className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          Historial de Partidos ({matches.length})
        </h3>
      </div>

      {Object.keys(matchesByCycle)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map((cycleNum) => (
          <div key={cycleNum} className="space-y-4">
            {/* Encabezado del Ciclo */}
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-8 h-8 bg-gradient-primary rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">C{cycleNum}</span>
              </div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Ciclo {cycleNum}
              </h4>
              <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700"></div>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                {matchesByCycle[cycleNum].length} partido(s)
              </span>
            </div>

            {/* Lista de Partidos */}
            <div className="space-y-3">
              {matchesByCycle[cycleNum].map((match) => (
                <motion.div
                  key={match.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-4 rounded-lg cursor-pointer transition-all ${getResultColor(match.result)} hover:shadow-md`}
                  onClick={() => setExpandedMatch(expandedMatch === match.id ? null : match.id)}
                >
                  {/* Header del Partido */}
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                    {/* Primera línea: Paso y Equipos */}
                    <div className="flex items-start md:items-center space-x-3 flex-1 min-w-0">
                      {/* Paso */}
                      <div className="w-10 h-10 bg-white dark:bg-gray-800 rounded-lg flex items-center justify-center font-bold text-gray-900 dark:text-gray-100 shadow-sm flex-shrink-0">
                        P{match.step}
                      </div>

                      {/* Equipos - Responsive */}
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col md:flex-row md:items-center md:space-x-2 gap-1 md:gap-0">
                          <span className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                            {match.localTeam?.name || 'Local'}
                          </span>
                          <span className="text-xs bg-white/50 dark:bg-gray-700/50 px-2 py-1 rounded w-fit">
                            {match.localTeam?.score ?? '-'}
                          </span>
                          <span className="text-gray-500 dark:text-gray-400 text-sm">vs</span>
                          <span className="text-xs bg-white/50 dark:bg-gray-700/50 px-2 py-1 rounded w-fit">
                            {match.visitorTeam?.score ?? '-'}
                          </span>
                          <span className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                            {match.visitorTeam?.name || 'Visitante'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Segunda línea: Resultado - Abajo en móvil, derecha en desktop */}
                    <div className="flex items-center space-x-2 flex-wrap gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getResultBadgeColor(match.result)}`}>
                        {getResultLabel(match.result)}
                      </span>
                      {match.result === 'draw' && match.payoutFactor && (
                        <div className="flex items-center space-x-1 bg-green-100 dark:bg-green-900/30 px-3 py-1 rounded-lg whitespace-nowrap">
                          <Target className="w-4 h-4 text-green-600 dark:text-green-400 flex-shrink-0" />
                          <span className="text-sm font-semibold text-green-800 dark:text-green-300">
                            {match.payoutFactor}x
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Detalles Expandidos */}
                  {expandedMatch === match.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 pt-4 border-t border-gray-300 dark:border-gray-600 space-y-3"
                    >
                      {/* Información General */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wide font-semibold">
                            Nombre del Partido
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-1">
                            {match.matchName || 'Sin nombre'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-600 dark:text-gray-400 uppercase tracking-wide font-semibold">
                            Fecha
                          </p>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mt-1">
                            {match.createdAt ? new Date(match.createdAt).toLocaleString('es-ES') : 'Sin fecha'}
                          </p>
                        </div>
                      </div>

                      {/* Detalles de Apuestas */}
                      {match.result === 'draw' && (
                        <div className="bg-white/40 dark:bg-gray-700/40 rounded p-3">
                          <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
                            Detalles de Empate
                          </h5>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <p className="text-xs text-gray-600 dark:text-gray-400">Factor de Ganancia</p>
                              <p className="text-sm font-bold text-green-700 dark:text-green-300">
                                {match.payoutFactor}x
                              </p>
                            </div>
                            {match.drawOdds && (
                              <div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">Cuota de Empate</p>
                                <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                  {match.drawOdds}
                                </p>
                              </div>
                            )}
                            {match.maxNonDraw && (
                              <div>
                                <p className="text-xs text-gray-600 dark:text-gray-400">Máximo Sin Empate</p>
                                <p className="text-sm font-bold text-gray-900 dark:text-gray-100">
                                  {match.maxNonDraw}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
};

export default EventMatches;
